import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { Observable, Subscription } from 'rxjs';
import { IItemsServiceInterface } from '../../core/interfaces/items/items-service.interface';
import { ActivatedRoute, Router, RouteReuseStrategy } from '@angular/router';
import { ToastService } from '../../core/services/toast-service';
import { TranslateService } from '@ngx-translate/core';
import { TextKeys } from '../../core/i18n/text-keys';
import { MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { IItemsQueryInterface } from 'src/app/core/interfaces/items/items-query.interface';
import { LocalStoreConfigModel } from '../../core/models/local-store-config.model';
import { UntypedFormControl } from '@angular/forms';

@Component({
  selector: 'app-input-with-selection-list',
  templateUrl: './input-with-selection-list.component.html',
  styleUrls: ['./input-with-selection-list.component.scss'],
})
export class InputWithSelectionListComponent<T> implements OnInit, OnDestroy {
  @ViewChild('inputFieldWithSelection') inputFieldWithSelection;
  @ViewChild(MatAutocompleteTrigger) autoCompleteItem: MatAutocompleteTrigger;

  @Input() labelTextKey: string;
  @Input() itemsService: IItemsServiceInterface<T>;
  @Input() storeConfig: LocalStoreConfigModel;
  @Input() itemsQuery: IItemsQueryInterface<T>;
  @Input() listRoute: string;
  @Input() routeStateParams: any;
  @Input() disabled$: Observable<boolean>;
  @Input() clear$: Observable<void>;
  @Input() key: string;
  @Input() inputFormControl: UntypedFormControl;
  @Input() initItemText: string;

  @Output() itemFocusOut: EventEmitter<T> = new EventEmitter<T>();
  @Output() itemSelected: EventEmitter<T> = new EventEmitter<T>();

  inputData$: Observable<T[]>;

  private subscriptions = new Subscription();
  private focused = false;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private translateService: TranslateService,
    private toastService: ToastService
  ) {}

  ngOnInit() {
    if (!this.inputFormControl) {
      this.inputFormControl = new UntypedFormControl();
    }

    this.itemsService.initializeStore(this.storeConfig);
    this.inputData$ = this.itemsQuery.selectItems();
    if (this.clear$) {
      this.subscriptions.add(
        this.clear$.subscribe(() => {
          this.inputFormControl.setValue('');
        })
      );
    }

    if (this.disabled$) {
      this.subscriptions.add(
        this.disabled$.subscribe((val) => {
          if (!val) {
            this.inputFormControl.enable();
          } else {
            this.inputFormControl.disable();
          }
        })
      );
    }

    this.subscriptions.add(
      this.itemsQuery.selectActive().subscribe((item) => {
        if (item) {
          this.itemSelected.emit(item);
          this.inputFormControl.setValue(
            this.itemsQuery.getItemValue(item, this.key)
          );
        }
      })
    );

    if (this.initItemText) {
      this.setValue(this.initItemText);
    }
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }

  getValue(): string {
    return this.inputFormControl.value;
  }

  setValue(value: string) {
    console.log('setValue', value);
    this.inputFormControl.setValue(value);
  }

  onInput(event) {
    this.itemsService.changeSearchFilterValue(
      event.target.value,
      this.storeConfig
    );
  }

  onItemSelected(event, item: T) {
    this.itemsService.setActive(item, this.storeConfig);
  }

  onFocus(event) {
    if (!this.focused) {
      this.focused = true;
      event.stopImmediatePropagation();
      this.itemsService.initializeStore(this.storeConfig);
    }
  }

  onFocusOut() {
    this.focused = false;
    if (this.itemsQuery.getActive()) {
      this.inputFieldWithSelection = this.itemsQuery.getActiveText();
    }
    this.itemsService.clearSearchFilterValue(this.storeConfig);

    this.itemFocusOut.emit(this.itemsQuery.getActive());
  }

  onOpenListClick(event) {
    event.stopImmediatePropagation();
    this.autoCompleteItem.closePanel();
    this.itemsService.clearSearchFilterValue(this.storeConfig);

    this.router
      .navigate([this.listRoute], {
        relativeTo: this.route,
        state: this.routeStateParams,
      })
      .catch((error) => {
        console.log(error);
        this.toastService.presentErrorToast({
          message: this.translateService.instant(
            TextKeys.general.naviationFailed
          ),
        });
      });
  }

  setFocus() {
    if (this.inputFieldWithSelection?.nativeElement) {
      this.inputFieldWithSelection.nativeElement.focus();
    }
  }
}
